import { Box, Option, Textarea } from "@mui/joy";
import DarkInput from "components/common/DarkInput";
import DarkSelect from "components/common/DarkSelect";
import { ColorPaletteSelect } from "components/common/ColorPalette";
import type { ExcursionStatus } from "api/excursions/Excursion.typed";
import {
  ExcursionStatusPending,
  ExcursionStatusDone,
  ExcursionStatusCancelled,
} from "api/excursions/Excursion.typed";
import { getExcursionStatusEmoji } from "../../../excursions/shared/utils/planningUtils";
import { colorTokens, darkBorder, textTokens } from "styles/globalStyles";
import { templateDisplayName } from "../../../excursions/shared/utils/excursionUtils";
import { COLOR_PALETTE_EXCURSION, EXCURSION_DEFAULT_COLOR } from "utils/colorPalette";

function statusEmojiBlock(selected: ExcursionStatus) {
  const emoji = getExcursionStatusEmoji(selected);
  const labels: Record<string, string> = {
    [ExcursionStatusPending]: "Planifiée",
    [ExcursionStatusDone]: "Validée",
    [ExcursionStatusCancelled]: "Annulée",
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
      {emoji}
      <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
        {labels[selected] ?? selected}
      </Box>
    </Box>
  );
}

export type ExcursionModalHeaderProps = {
  name: string;
  onNameChange: (value: string) => void;
  color: string | null;
  onColorChange: (hex: string) => void;
  status: ExcursionStatus;
  onStatusChange: (status: ExcursionStatus) => void;
  canEditDetails: boolean;
  canEditStatus: boolean;
  showStatus?: boolean;
  cancellationReason?: string;
  onCancellationReasonChange?: (value: string) => void;
  /** When true, show optional cancellation motive field (status is cancelled). */
  showCancellationMotif?: boolean;
  /** When false, motive is read-only in the field. */
  canEditCancellationMotif?: boolean;
  excursionTemplates?: { id?: number; name?: string }[];
  selectedExcursionTemplateId?: number | null;
  onExcursionTemplateChange?: (id: number | null) => void;
};

export default function ExcursionModalHeader({
  name,
  onNameChange,
  color,
  onColorChange,
  status,
  onStatusChange,
  canEditDetails,
  canEditStatus,
  showStatus = true,
  cancellationReason = "",
  onCancellationReasonChange,
  showCancellationMotif = false,
  canEditCancellationMotif = false,
  excursionTemplates,
  selectedExcursionTemplateId,
  onExcursionTemplateChange,
}: ExcursionModalHeaderProps) {
  const effectiveColor = color || EXCURSION_DEFAULT_COLOR;
  const readOnlyDetails = !canEditDetails;

  const titleInput = (
    <DarkInput
      value={name}
      onChange={(e) => onNameChange(e.target.value)}
      placeholder="Nom de la sortie"
      disabled={!canEditDetails}
      slotProps={{
        input: {
          sx: {
            fontSize: { xs: "0.9rem", sm: "1.1rem" },
            fontWeight: 600,
            color: textTokens.color.primary,
          },
        },
      }}
      sx={{ minHeight: { xs: 36, sm: 40 } }}
    />
  );

  const paletteControl = (
    <ColorPaletteSelect
      value={effectiveColor}
      onChange={onColorChange}
      disabled={!canEditDetails}
      sx={
        readOnlyDetails
          ? { flexShrink: 0, minWidth: 0 }
          : { minWidth: 0, flex: { xs: "1 1 0", sm: "1 1 auto" } }
      }
      palette={COLOR_PALETTE_EXCURSION}
      defaultColor={EXCURSION_DEFAULT_COLOR}
    />
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: { xs: 1, sm: 1.5 },
        mb: { xs: 1, sm: 2 },
        pb: { xs: 1, sm: 2 },
        borderBottom: darkBorder,
      }}
    >
      {showStatus && (
        <Box
          sx={{
            alignSelf: "stretch",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <DarkSelect
            value={status}
            onChange={(_, v) => {
              if (v != null) onStatusChange(v as ExcursionStatus);
            }}
            disabled={!canEditStatus}
            sx={{
              width: "100%",
              minWidth: 0,
              alignSelf: "stretch",
              "& .MuiSelect-button": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
            }}
            renderValue={(option) => {
              const v = (option?.value ?? status) as ExcursionStatus;
              return statusEmojiBlock(v);
            }}
          >
            <Option value={ExcursionStatusPending}>
              {getExcursionStatusEmoji(ExcursionStatusPending)} Planifiée
            </Option>
            <Option value={ExcursionStatusDone}>
              {getExcursionStatusEmoji(ExcursionStatusDone)} Validée
            </Option>
            <Option value={ExcursionStatusCancelled}>
              {getExcursionStatusEmoji(ExcursionStatusCancelled)} Annulée
            </Option>
          </DarkSelect>
          {showCancellationMotif && (
            <Textarea
              placeholder="Motif d'annulation (optionnel)"
              value={cancellationReason}
              onChange={(event) => onCancellationReasonChange?.(event.target.value.slice(0, 4000))}
              disabled={!canEditCancellationMotif}
              minRows={2}
              maxRows={5}
              slotProps={{
                textarea: {
                  maxLength: 4000,
                },
              }}
              sx={{
                width: "100%",
                alignSelf: "stretch",
                minWidth: 0,
                backgroundColor: colorTokens.bg.surfaceMuted,
                color: textTokens.color.primary,
                borderRadius: "8px",
                border: `1px solid ${colorTokens.border.strong}`,
                "&:focus-within": {
                  borderColor: colorTokens.border.focus,
                },
              }}
            />
          )}
        </Box>
      )}
      {readOnlyDetails ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.75, sm: 1 },
            width: "100%",
            minWidth: 0,
            flexWrap: { xs: "wrap", sm: "nowrap" },
          }}
        >
          <Box sx={{ flex: "1 1 0", minWidth: 0, minHeight: 0 }}>{titleInput}</Box>
          {paletteControl}
        </Box>
      ) : (
        <>
          <Box sx={{ flexBasis: "100%", flexGrow: 2, minWidth: 0, width: "100%" }}>
            {titleInput}
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
              minWidth: 0,
              width: "100%",
            }}
          >
            {excursionTemplates &&
              excursionTemplates.length >= 1 &&
              canEditDetails &&
              onExcursionTemplateChange && (
                <DarkSelect
                  value={selectedExcursionTemplateId ?? ""}
                  onChange={(_, v) => onExcursionTemplateChange(v != null ? Number(v) : null)}
                  sx={{
                    minWidth: 0,
                    flex: { xs: "2 1 0", sm: "2 1 auto" },
                    "& .MuiSelect-button": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  {excursionTemplates.map((t, idx) => (
                    <Option key={t.id ?? idx} value={t.id ?? 0}>
                      {templateDisplayName(t)}
                    </Option>
                  ))}
                </DarkSelect>
              )}
            {paletteControl}
          </Box>
        </>
      )}
    </Box>
  );
}
